import { useState, useEffect, useCallback } from 'react';
import {
  getTaskList,
  TrackExportTaskListResponse,
} from '../services/trackExportApi';

const POLL_INTERVAL_MS = 2000; // 2 秒轮询以保持进度条与列表实时同步
const POLL_INTERVAL_URGENT_MS = 1000; // 进度条显示时 1 秒轮询，更快同步
const POLL_AFTER_UPLOAD_MS = 3 * 60 * 1000; // 上传后持续轮询 3 分钟

export function useTrackExportTasks(params: {
  page?: number;
  size?: number;
  status?: number;
  operatorId?: number;
  enablePolling?: boolean;
  /** 进度条是否显示中（有当前任务），为 true 时使用更短轮询间隔 */
  hasActiveProgressBar?: boolean;
  /** 为 false 时不请求列表、不轮询（无权限等场景） */
  enabled?: boolean;
}) {
  const enabled = params.enabled !== false;
  const [data, setData] = useState<TrackExportTaskListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUploadTime, setLastUploadTime] = useState(0);

  const fetchTasks = useCallback(
    async (overrides?: { page?: number; size?: number }) => {
      try {
        setError(null);
        const result = await getTaskList({
          page: overrides?.page ?? params.page ?? 1,
          size: overrides?.size ?? params.size ?? 20,
          status: params.status,
          operatorId: params.operatorId,
        });
        setData(result);
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        setLoading(false);
      }
    },
    [params.page, params.size, params.status, params.operatorId]
  );

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchTasks();
  }, [fetchTasks, enabled]);

  const hasParsingTask = data?.content?.some((t) => t.status === 0) ?? false;
  const allTasksCompleted =
    (data?.content?.length ?? 0) > 0 &&
    (data?.content?.every((t) => t.status !== 0) ?? false);
  const withinPollAfterUpload =
    lastUploadTime > 0 &&
    Date.now() - lastUploadTime < POLL_AFTER_UPLOAD_MS;
  const hasActiveProgressBar = params.hasActiveProgressBar ?? false;
  const shouldPoll =
    params.enablePolling &&
    (hasParsingTask ||
      hasActiveProgressBar ||
      (withinPollAfterUpload && !allTasksCompleted));

  const intervalMs =
    params.hasActiveProgressBar && shouldPoll
      ? POLL_INTERVAL_URGENT_MS
      : POLL_INTERVAL_MS;

  useEffect(() => {
    if (!enabled || !shouldPoll) return;

    const timer = setInterval(() => fetchTasks(), intervalMs);
    return () => clearInterval(timer);
  }, [enabled, shouldPoll, fetchTasks, intervalMs]);

  const triggerUploadSuccess = useCallback(() => {
    setLastUploadTime(Date.now());
  }, []);

  const refetch = useCallback(
    (overrides?: { page?: number; size?: number }) => {
      fetchTasks(overrides);
    },
    [fetchTasks]
  );

  return {
    data,
    loading,
    error,
    refetch,
    triggerUploadSuccess,
  };
}
